import React from 'react';
import { Grid, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from '../../components/MainCard';
import { useTranslation } from 'react-i18next';

interface ReportSummaryCardsProps {
  totalIncome: number;
  totalExpenses: number;
  currency: string;
  isLoading: boolean;
}

interface SummaryCardProps {
  label: string;
  amount: number;
  currency: string;
  color?: string;
  isLoading: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, amount, currency, color, isLoading }) => {
  return (
    <MainCard>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width="80%" height={40} />
      ) : (
        <Typography variant="h4" fontWeight={600} sx={{ color, mt: 0.5 }}>
          {currency} {amount.toFixed(2)}
        </Typography>
      )}
    </MainCard>
  );
};

const ReportSummaryCards: React.FC<ReportSummaryCardsProps> = ({
  totalIncome,
  totalExpenses,
  currency,
  isLoading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const netBalance = totalIncome - totalExpenses;

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} sm={4}>
        <SummaryCard
          label={t('report.total_income')}
          amount={totalIncome}
          currency={currency}
          color={theme.palette.success.main}
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SummaryCard
          label={t('report.total_expenses')}
          amount={totalExpenses}
          currency={currency}
          color={theme.palette.error.main}
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <SummaryCard
          label={t('report.net_balance')}
          amount={netBalance}
          currency={currency}
          color={netBalance >= 0 ? theme.palette.success.main : theme.palette.error.main}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default ReportSummaryCards;
